import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Play, Trophy, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../context/I18nContext';
import UserAvatar from '../components/UserAvatar';
import { formatAura, getLevelForAura } from '../lib/aura';

/**
 * Complete battle experience:
 *   preparing → active (3-2-1 countdown) → voting → closed/tied
 * Polls the server every 1s (server time is authoritative).
 * Never displays vote counts until the server reveals them (status closed/tied).
 */
export default function BattleDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { t } = useI18n();
  const [battle, setBattle] = useState(null);
  const [tick, setTick] = useState(0);
  const [voting, setVoting] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const mountedRef = useRef(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/battles/${id}`);
      if (mountedRef.current) setBattle(data);
    } catch (err) {
      if (mountedRef.current) toast.error(err?.response?.data?.detail || t('common.error'));
    }
  }, [id, t]);

  useEffect(() => {
    mountedRef.current = true;
    load();
    const interval = setInterval(() => { load(); setTick((n) => n + 1); }, 1200);
    return () => { mountedRef.current = false; clearInterval(interval); };
  }, [load]);

  const serverNow = useMemo(() => battle ? new Date(battle.server_now).getTime() : Date.now(), [battle]);
  const localOffset = useMemo(() => Date.now() - serverNow, [serverNow]);
  const nowServer = Date.now() - localOffset;

  const openVoting = async () => {
    try { await api.post(`/battles/${id}/open-voting`, {}); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || t('common.error')); }
  };
  const startBattle = async () => {
    try { await api.post(`/battles/${id}/start`, {}); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || t('common.error')); }
  };
  const castVote = async (competitorId) => {
    if (voting) return;
    setVoting(true);
    try {
      await api.post(`/battles/${id}/vote`, { selected_competitor_id: competitorId });
      setConfirming(true);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('common.error'));
    } finally { setVoting(false); }
  };

  if (!battle) {
    return <div className="min-h-screen grid place-items-center bg-aura-bg"><div className="text-4xl animate-aura-pulse">⚡</div></div>;
  }

  const a = battle.competitor_a;
  const b = battle.competitor_b;
  const isParticipant = battle.viewer?.is_participant;
  const isModerator = user && ((user.roles || []).includes('admin') || (user.roles || []).includes('moderator'));

  // Countdowns based on server time
  const battleStartMs = battle.battle_start_time ? new Date(battle.battle_start_time).getTime() : null;
  const votingCloseMs = battle.voting_close_time ? new Date(battle.voting_close_time).getTime() : null;
  const secToStart = battleStartMs ? Math.max(0, Math.ceil((battleStartMs - nowServer) / 1000)) : 0;
  const secToClose = votingCloseMs ? Math.max(0, Math.ceil((votingCloseMs - nowServer) / 1000)) : 0;

  const showResults = ['closed', 'completed', 'tied', 'cancelled'].includes(battle.status);
  const isTied = battle.status === 'tied';
  const winnerIsA = battle.winner_id && battle.winner_id === a.user_id;
  const winnerIsB = battle.winner_id && battle.winner_id === b.user_id;

  return (
    <div className="min-h-screen bg-aura-bg pb-16 relative overflow-hidden" data-testid={`battle-${id}`}>
      <div className="absolute inset-0 bg-aura-radial pointer-events-none" />

      <header className="relative flex items-center justify-between px-5 pt-6">
        <button onClick={() => nav('/battles')} className="text-aura-textDim hover:text-white flex items-center gap-1" data-testid="btn-back">
          <ChevronLeft size={20} /> {t('common.back')}
        </button>
        <div className="text-[10px] uppercase tracking-[0.3em] text-aura-textDim">
          <span className="text-aura-cyan mr-1 font-display font-bold">{battle.source?.toUpperCase() || 'QR'}</span>
          · {battle.voting_type?.toUpperCase()}
        </div>
        <span className="w-8" />
      </header>

      <main className="relative mx-auto max-w-md px-5 pt-4">
        {/* Title */}
        <div className="text-center mb-4">
          <div className="font-display font-black text-3xl aura-text-gradient tracking-tight">⚡ AURA BATTLE ⚡</div>
          {battle.status === 'preparing' && (
            <div className="text-[10px] uppercase tracking-widest text-aura-textDim mt-1">{t('battle.preparing')}</div>
          )}
          {battle.status === 'active' && (
            <div className="text-[10px] uppercase tracking-widest text-aura-cyan mt-1">🔥 {t('battle.liveNow')}</div>
          )}
          {battle.status === 'voting' && (
            <div className="text-[10px] uppercase tracking-widest text-aura-yellow mt-1">🗳️ {t('battle.votingOpen')}</div>
          )}
        </div>

        {/* Countdown (preparing) */}
        {battle.status === 'preparing' && secToStart <= 3 && secToStart > 0 && (
          <div className="grid place-items-center py-8 animate-grow-in">
            <div className="font-display font-black text-8xl aura-text-gradient">{secToStart}</div>
            <div className="text-white/70 tracking-widest text-sm">{t('battle.startsIn')}</div>
          </div>
        )}
        {battle.status === 'preparing' && secToStart === 0 && (
          <div className="grid place-items-center py-8"><div className="font-display font-black text-6xl aura-text-gradient animate-aura-pulse">BATTLE!</div></div>
        )}

        {/* Stake / Pot indicator */}
        {battle.stake_amount > 0 && (
          <section className="mt-3 aura-card p-3 border-aura-gold/40 bg-gradient-to-r from-aura-gold/10 to-transparent" data-testid="battle-stake">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] font-display font-black text-aura-gold">POT EN JUEGO</div>
                <div className="font-mono font-black text-2xl text-white leading-none mt-1">
                  {(battle.stake_pot || (battle.stake_amount * ((battle.team_a?.length || 1) + (battle.team_b?.length || 1)))).toLocaleString()}
                  <span className="text-sm text-aura-gold ml-1">AURA</span>
                </div>
                <div className="text-[10px] text-aura-textDim mt-0.5">Stake por persona: {battle.stake_amount.toLocaleString()} · el ganador se lleva TODO</div>
              </div>
              <div className="text-3xl">💰</div>
            </div>
          </section>
        )}

        {/* Contestant cards */}
        <section className="grid grid-cols-11 items-center gap-2 mt-2" data-testid="battle-contestants">
          <ContestantCard c={a} highlight={winnerIsA} tied={isTied} testId="contestant-a" />
          <div className="col-span-1 text-center font-display font-black text-3xl aura-text-gradient">VS</div>
          <ContestantCard c={b} highlight={winnerIsB} tied={isTied} testId="contestant-b" />
        </section>

        {/* Preparing → Start button for participants */}
        {battle.status === 'preparing' && isParticipant && secToStart <= 0 && (
          <button onClick={startBattle} data-testid="btn-start" className="mt-6 aura-btn-primary w-full h-12">
            <Play size={16} /> {t('battle.startBattle')}
          </button>
        )}

        {/* Active → open voting */}
        {battle.status === 'active' && (isParticipant || isModerator) && (
          <button onClick={openVoting} data-testid="btn-open-voting" className="mt-6 aura-btn-primary w-full h-12">
            🗳️ {t('battle.openVoting')}
          </button>
        )}

        {/* Voting */}
        {battle.status === 'voting' && (
          <>
            <div className="text-center mt-6" data-testid="voting-timer">
              <div className="text-[10px] uppercase tracking-[0.3em] text-aura-textDim">{t('battle.closesIn')}</div>
              <div className="font-display font-black text-4xl aura-text-gradient">{secToClose}s</div>
            </div>

            {battle.viewer?.has_voted ? (
              <div className="mt-6 aura-card p-6 text-center" data-testid="voted-message">
                <div className="text-3xl">✅</div>
                <div className="font-display font-bold text-white mt-1">{t('battle.voteRecorded')}</div>
                <div className="text-xs text-aura-textDim">{t('battle.waitResults')}</div>
              </div>
            ) : battle.viewer?.can_vote ? (
              <div className="mt-6 grid grid-cols-2 gap-3" data-testid="vote-buttons">
                <button onClick={() => castVote(a.user_id)} disabled={voting} data-testid="vote-a" className="rounded-2xl border border-aura-cyan/50 py-4 hover:bg-aura-cyan/10 disabled:opacity-50">
                  <div className="font-display font-bold text-white text-sm">{t('battle.voteFor')}</div>
                  <div className="text-aura-cyan font-display font-black">@{a.username}</div>
                </button>
                <button onClick={() => castVote(b.user_id)} disabled={voting} data-testid="vote-b" className="rounded-2xl border border-aura-violet/50 py-4 hover:bg-aura-violet/10 disabled:opacity-50">
                  <div className="font-display font-bold text-white text-sm">{t('battle.voteFor')}</div>
                  <div className="text-aura-violet font-display font-black">@{b.username}</div>
                </button>
              </div>
            ) : (
              <div className="mt-6 aura-card p-4 text-center text-sm text-aura-textDim">
                {isParticipant ? t('battle.contestantsCannotVote') : t('battle.cannotVote')}
              </div>
            )}
          </>
        )}

        {/* Results */}
        {showResults && (
          <section className="mt-6 space-y-4 animate-grow-in" data-testid="battle-results">
            {isTied ? (
              <div className="aura-card p-6 text-center">
                <div className="text-5xl mb-2">⚖️</div>
                <div className="font-display font-black text-3xl aura-text-gradient">{t('battle.tied')}</div>
                <div className="text-xs text-aura-textDim mt-1">{t('battle.tiedDesc')}</div>
              </div>
            ) : (
              <div className="aura-card p-6 text-center relative overflow-hidden grain">
                <div className="absolute inset-0 bg-aura-radial opacity-50 pointer-events-none" />
                <Trophy size={40} className="mx-auto text-aura-yellow animate-aura-pulse" />
                <div className="mt-2 text-[10px] uppercase tracking-[0.3em] text-aura-textDim">{t('battle.winner')}</div>
                <div className="mt-1 font-display font-black text-3xl aura-text-gradient">
                  @{winnerIsA ? a.username : b.username}
                </div>
                <div className="mt-1 text-sm text-white/80 font-mono">
                  {winnerIsA ? battle.votes_a : battle.votes_b} {t('battle.votes')} · {winnerIsA ? battle.percent_a : battle.percent_b}%
                </div>
              </div>
            )}

            {/* Breakdown */}
            <div className="aura-card p-4">
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="font-display font-bold text-white">@{a.username}</span>
                <span className="text-aura-cyan font-mono">{battle.votes_a} · {battle.percent_a}%</span>
              </div>
              <div className="h-2 rounded-full bg-white/5 overflow-hidden">
                <div className="h-full bg-aura-gradient" style={{ width: `${battle.percent_a || 0}%` }} />
              </div>
              <div className="flex items-center justify-between text-sm mt-3 mb-2">
                <span className="font-display font-bold text-white">@{b.username}</span>
                <span className="text-aura-violet font-mono">{battle.votes_b} · {battle.percent_b}%</span>
              </div>
              <div className="h-2 rounded-full bg-white/5 overflow-hidden">
                <div className="h-full bg-gradient-to-r from-aura-violet to-aura-pink" style={{ width: `${battle.percent_b || 0}%` }} />
              </div>
              <div className="mt-3 text-[10px] uppercase tracking-widest text-aura-textDim text-center">
                {battle.total_votes} {t('battle.totalVotes')}
              </div>
            </div>

            {/* Aura awarded */}
            {battle.aura_awarded && Object.keys(battle.aura_awarded).length > 0 && (
              <div className="aura-card p-4" data-testid="aura-awarded">
                <div className="flex items-center gap-2 mb-3">
                  <Zap size={14} className="text-aura-cyan" />
                  <div className="font-display font-bold text-sm tracking-wider">{t('battle.auraAwarded')}</div>
                </div>
                {[a, b].map((c) => {
                  const amt = battle.aura_awarded[c.user_id] || 0;
                  return (
                    <div key={c.user_id} className="flex items-center justify-between text-sm py-1">
                      <span>@{c.username}</span>
                      <span className="font-mono text-aura-green">+{formatAura(amt)}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        )}
      </main>
    </div>
  );
}

function ContestantCard({ c, highlight, tied, testId }) {
  const level = getLevelForAura(c?.aura_points || 0);
  return (
    <div className={`col-span-5 relative aura-card p-3 text-center ${highlight ? 'ring-2 ring-aura-yellow shadow-neon-yellow' : ''}`} data-testid={testId}>
      {highlight && <Trophy size={16} className="absolute -top-2 -right-2 text-aura-yellow" />}
      <UserAvatar user={c} size="lg" className="mx-auto" />
      <div className="mt-2 font-display font-bold text-white text-sm truncate">{c.name}</div>
      <div className="text-[10px] text-aura-textDim truncate">@{c.username}</div>
      <div className="mt-2 text-xs text-aura-cyan font-mono">⚡{formatAura(c.aura_points || 0)}</div>
      <div className="mt-0.5 text-[10px] font-display font-bold" style={{ color: level.color }}>{level.name}</div>
      <div className="mt-2 flex justify-center gap-2 text-[10px] text-aura-textDim">
        <span>🔥{c.current_streak || 0}</span>
        <span>🏆{c.wins || 0}</span>
      </div>
    </div>
  );
}
